import React, { useId, useMemo, useState } from 'react';
import { Plus, MessageCircleQuestion } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useRevealGroup } from '../hooks/useReveal';

/**
 * The questions that arrive in the WhatsApp inbox before someone pays.
 *
 * Support details and the mentor are read from the live settings rather than
 * written into the copy, so the answers keep matching the workshop the page is
 * currently selling instead of going stale the next time a batch changes.
 */
function buildFaqs(workshop, adminSettings) {
  const whatsappDigits = String(adminSettings.supportWhatsapp || '').replace(/\D/g, '');

  const items = [
    {
      id: 'live-session',
      question: 'How do I watch the live session?',
      answer: (
        <>
          Once you enroll, you will be auto redirected to the WhatsApp group. You will get the
          class link (Zoom link) before your workshop starts.
        </>
      ),
    },
    {
      id: 'ask',
      question: 'I have some questions. Where can I ask?',
      answer: (
        <>
          Please WhatsApp us at{' '}
          <a
            href={whatsappDigits ? `https://wa.me/${whatsappDigits}` : '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-700 underline underline-offset-2"
          >
            {adminSettings.supportWhatsapp}
          </a>{' '}
          or email us at{' '}
          <a
            href={`mailto:${adminSettings.supportEmail}`}
            className="font-semibold text-brand-700 underline underline-offset-2 break-all"
          >
            {adminSettings.supportEmail}
          </a>
          .
        </>
      ),
    },
    {
      id: 'device',
      question: 'Can I join with my Android phone/laptop?',
      answer: <>Yes!</>,
    },
    {
      id: 'hours',
      question: 'During the workshop, how many hours do I have to give everyday?',
      answer: <>Everyday you need to give 90 minutes.</>,
    },
    {
      id: 'mentor',
      question: 'Who will be the mentor in this workshop?',
      answer: (
        <>
          {workshop?.instructor && workshop.instructor !== 'Ritu Malhotra'
            ? `${workshop.instructor}${workshop.instructorTitle ? `, ${workshop.instructorTitle}` : ''}.`
            : 'Raja Mandal, Corporate Communication Coach.'}
        </>
      ),
    },
  ];

  return items;
}

/**
 * One question. Collapsed it is a row; open it is a card with its answer.
 *
 * The panel animates on `grid-template-rows` rather than on a measured pixel
 * height, so an answer that reflows — a long support address wrapping on a
 * narrow phone — still opens to exactly its own height. Collapsed panels are
 * `inert`: the answers carry links, and a link inside a shut drawer must not
 * be reachable by tab or read out by a screen reader.
 */
function FaqRow({ item, open, onToggle }) {
  const panelId = `${useId()}-panel`;

  return (
    <li className={`card overflow-hidden ${open ? 'card-feature' : ''}`}>
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          className="w-full flex items-start justify-between gap-3.5 p-5 text-left"
        >
          <span className="text-[0.88rem] font-bold leading-snug text-ink-900">
            {item.question}
          </span>
          <span
            className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center border transition-colors duration-300 ${
              open
                ? 'bg-brand-600 border-brand-600 text-white'
                : 'bg-ink-100 border-ink-200 text-ink-500'
            }`}
            aria-hidden="true"
          >
            <Plus
              className={`w-3.5 h-3.5 stroke-[2.5] transition-transform duration-300 ${
                open ? 'rotate-45' : ''
              }`}
            />
          </span>
        </button>
      </h3>

      <div
        id={panelId}
        inert={!open}
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-5 -mt-1">
            <span className="block w-8 h-px mb-3 bg-gradient-to-r from-brand-600 to-gold-500" />
            <p className="text-[0.82rem] text-ink-500 leading-relaxed">{item.answer}</p>
          </div>
        </div>
      </div>
    </li>
  );
}

/**
 * The last objections, answered after the price and before the footer.
 *
 * One row open at a time: the list stays short enough to scan on a phone, and
 * an opened answer never pushes the question the visitor is reading off the
 * top of the screen.
 */
export const FaqSection = ({ workshop }) => {
  const { adminSettings } = useApp();
  const faqs = useMemo(() => buildFaqs(workshop, adminSettings), [workshop, adminSettings]);

  const [openId, setOpenId] = useState(null);
  const listRef = useRevealGroup({ stagger: 60 });

  if (faqs.length === 0) return null;

  return (
    <section id="faq" className="section bg-[#fffdfb] text-ink-900">
      <div className="shell">
        <span className="pill pill-gold">
          <MessageCircleQuestion className="w-3.5 h-3.5" />
          Before you book
        </span>
        <h2 className="mt-3.5 font-display text-display-md font-bold text-balance">
          Frequently asked{' '}
          <span className="font-display-italic text-brand-700">questions</span>
        </h2>
        <p className="mt-2.5 text-sm text-ink-500 leading-relaxed">
          Tap any question to see the answer.
        </p>

        <ul ref={listRef} className="mt-6 space-y-3">
          {faqs.map((item) => (
            <FaqRow
              key={item.id}
              item={item}
              open={openId === item.id}
              onToggle={() => setOpenId((current) => (current === item.id ? null : item.id))}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};
